import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, ButtonModel } from '@notino/react-styleguide';
import { LoggedInUserReviewQuery } from '@notino/shared/definitions/types';

import { IReviewSummary } from '@containers/ProductDetailContainer/model';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { mobileVisibleMd } from '@styles/common';

import messages from '../../../../messages';

import { AttributesSummary } from './AttributesSummary';
import { ReviewHeader } from './ReviewHeader';
import { NoReviews } from './ReviewHeader/NoReviews';
import { useReviewModalOrDrawer } from './ReviewModal/useReviewModalOrDrawer';
import { StarsSummary } from './StarsSummary';
import { HeaderAndStarSummary } from './styled';

interface IReviewsSummaryProps {
  reviewSummary: IReviewSummary;
  productCode: string;
  isAllReviewsPage?: boolean;
  masterId: string;
  catalogMasterId: string;
  productReviewCount: number;
  loggedInUserReview?: LoggedInUserReviewQuery['loggedInUserReview'];
  refetchLoggedInUserReview: () => Promise<void>;
}

export const ReviewsSummary: React.FC<IReviewsSummaryProps> = React.memo(
  ({
    reviewSummary,
    productCode,
    masterId,
    catalogMasterId,
    isAllReviewsPage,
    loggedInUserReview,
    refetchLoggedInUserReview,
    productReviewCount,
  }) => {
    const { getTimeFromInit } = useTrackingContext();

    const openReviewModalOrDrawer = useReviewModalOrDrawer({
      ratings: reviewSummary.ratings,
      productCode,
      masterId,
      catalogMasterId,
      isAllReviewsPage,
      loggedInUserReview,
      refetchLoggedInUserReview,
    });

    const handleAddNewReview = () => {
      dispatchTrackingEvent({
        event: 'element_click',
        element: {
          action: loggedInUserReview ? 'wt_edit_review' : 'wt_add_review',
          interaction: 'click',
          name: 'review',
          type: isAllReviewsPage ? 'review' : 'product_detail',
          mode: 'open',
          promo_labels: undefined,
          timing: getTimeFromInit(),
        },
        _clear: true,
      });

      openReviewModalOrDrawer();
    };

    if (!reviewSummary) {
      return null;
    }

    const hasReviews = reviewSummary.totalVotes > 0;

    return (
      <div>
        {hasReviews ? (
          <>
            <HeaderAndStarSummary>
              <ReviewHeader
                hasReviews={hasReviews}
                reviewSummary={reviewSummary}
                onWriteMessageClick={handleAddNewReview}
                isAllReviewsPage={isAllReviewsPage}
                userHasWrittenReview={!!loggedInUserReview}
                productReviewCount={productReviewCount}
              />
              <StarsSummary reviewSummary={reviewSummary} />
            </HeaderAndStarSummary>
            <AttributesSummary ratings={reviewSummary.ratings} />
            <div className={mobileVisibleMd}>
              <Button
                data-cypress="reviews-addNewReview"
                buttonStyle={ButtonModel.Styles.secondary}
                onClick={handleAddNewReview}
                fullWidth={true}
              >
                <FormattedMessage
                  {...(loggedInUserReview
                    ? messages.editReview
                    : messages.writeReview)}
                />
              </Button>
            </div>
          </>
        ) : (
          <NoReviews onWriteMessageClick={handleAddNewReview} />
        )}
      </div>
    );
  }
);
